import React, { useContext, useEffect, useRef, useState } from 'react';
import useFolders from '@hooks/use-folders/useFolders.tsx';
import InternalInfoFoldersTag from '@components/framework/tags/InternalInfoFoldersTag.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { buildCarrierNoteWithLocationBlock } from '@utils/carrierNoteMerge.ts';
import * as yup from 'yup';
import {
  AccessTypeEnum,
  CustomsTypeCodeEnum,
  EntityTypeEnum,
  ExternalOrdersApiTypeEnum,
  GetExternalOrdersResponse,
  LabelPreviewWithoutTypeOutDTO,
  LovDTO,
  PermissionTypesEnum,
  SnFolderSettingsDTO,
  SnPartyConfigDTO,
  TagEnum,
} from '@api/ringil-api.v2';
import { resolveSnTagConfig } from './snTagConfig';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';
import { ApiContext } from '@api/api';
import { buildSnManUnitTestId, buildSnOrderTestId, generateSnOrderConfig, makeSnOrderFieldSecured, mapSnOrder, SnFrameworkOrdersData } from './snFrameworkOrders';
import { isSnPapEnabled, snExternalIdPresent } from './useSnExternalOrders';
import { SupplierNotificationHeaderData } from './forms/SupplierNotificationHeaderForm';
import { SupplierNotificationCargoInfoData } from './forms/SupplierNotificationCargoInfoForm';
import { europeanUnion, isNotBlank, isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';
import useBEValidation from '@hooks/useBEValidation/useBEValidation';
import useForm, { proxiedPropertiesOf } from '@/hooks/useForm/useForm';
import useNavigation from '@hooks/useNavigation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import { activeDocNumber, isEntityOwner } from '@app/pages/customer/supplier-notification/utils.ts';
import AttachmentsSection from '@app/pages/attachment/AttachmentsSection.tsx';
import { UploadedAttachment } from '@components/ringil3/Features/Attachments/UploadAttachmentDialog.tsx';
import { timeslotValidationSchema } from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import { CargoTypeCodeEnum } from '@utils/constants.ts';
import { findValidationMessage } from '@utils/validation';
import { RoleEnum } from '@type/enums.ts';

import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import Card from '@components/framework/card/Card.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import SvgLkw from '@components/ringil3/icons/heading/Lkw.tsx';
import SvgMapPin from '@components/framework/icons/MapPin.tsx';
import SvgCalendar from '@app/pages/dap/icons/Calendar.tsx';
import SvgInternalInfoHeadling from '@components/framework/icons/InternalInfoHeadling.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';

import { InputCounterPartyNotifyPersonsIds } from '@components/obsolete/Form/InputSubscriberPlain/InputCounterPartyNotifyPersonsIds.tsx';
import LocationCard from '@components/ringil3/LocationCard/LocationCard.tsx';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import SecuredArea from '@components/security/SecuredArea';
import { POLICY_DESCRIPTION } from './snSecurityClauses';

import TransportationTypeTag from '@components/framework/tags/TransportationTypeTag.tsx';
import CargoTypeTag from '@components/framework/tags/CargoTypeTag.tsx';
import RequirementsTag from '@components/framework/tags/RequirementsTag.tsx';
import CarrierNoteTag from '@components/framework/tags/CarrierNoteTag.tsx';
import ShipmentStatusTag from '@components/framework/tags/ShipmentStatusTag.tsx';
import CustomsRegimeTag from '@components/framework/tags/CustomsRegimeTag.tsx';
import ContainerTag from '@components/framework/tags/ContainerTag.tsx';
import InputTextTag from '@components/RingilComponents/tags/InputTextTag.tsx';
import ResponsiblePersonTag from '@components/framework/tags/ResponsiblePersonTag.tsx';
import InternalInfoNotifyTag from '@components/framework/tags/InternalInfoNotifyTag.tsx';
import InternalInfoNotifyByRoleTag from '@components/framework/tags/InternalInfoNotifyByRoleTag.tsx';
import InternalInfoGoodsInvoiceTag, { GoodsInvoiceDTO } from '@components/framework/tags/InternalInfoGoodsInvoiceTag.tsx';
import IncotermCodeTag from '@components/framework/tags/IncotermCodeTag.tsx';
import InternalNoteTag from '@components/framework/tags/InternalNoteTag.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import OrdersComponent from '@components/framework/orders/OrdersComponent.tsx';
import { hasOnlyNoOrdersNow } from '@components/framework/orders/common.tsx';
import { createOrdersComponentValidationSchema } from '@components/framework/orders/validation.ts';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgAlert from '@icons/alert.svg?react';

// Decoupled, LovDTO-based working model for the redesigned create page.
// Internal-info holds LovDTO objects so the tag components can render names; converted to ids on save.
export interface SupplierNotificationForm extends SupplierNotificationHeaderData, SupplierNotificationCargoInfoData, SnFrameworkOrdersData {
  attachments: UploadedAttachment[];
  fromShipmentId?: string;
  internalNote?: string;
  counterPartyNotifyPersonsIds: string[];
  notifyData: { responsiblePerson?: LovDTO; userToNotify?: LovDTO[]; notifyUsersDependsOnRole: boolean };
  goodsInvoice?: GoodsInvoiceDTO;
  deliveryConditionsIncotermCode?: string;
  labels?: LabelPreviewWithoutTypeOutDTO[];
  cmrNumber?: string;
  blNumber?: string;
  awbNumber?: string;
  /** RD-2977: folders of the creating company */
  folders?: LovDTO[];
}

interface ComponentProps {
  initialData: SupplierNotificationForm;
}

// Only enum-backed tags can be referenced by visibleWhen/requiredWhen; resolve their current form value.
const tagValueOf = (v: SupplierNotificationForm) => (key: TagEnum): unknown => {
  switch (key) {
    case TagEnum.TRANSPORTATION_TYPE:
      return v.transportationType;
    case TagEnum.STATUS:
      return v.shipmentStateCode;
    case TagEnum.CARGO:
      return v.cargoType?.cargoTypeCode;
    case TagEnum.CUSTOMS_REGIME:
      return v.customsTypeCode;
    default:
      return undefined;
  }
};

const CreateSupplierNotification: React.FC<ComponentProps> = ({ initialData }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser, hasPermission } = useContext(AuthenticatedUserContext);
  const { redirectWithToastMessage } = useContext(ToastContext);
  const [loading, setLoading] = useState(false);

  const navigation = useNavigation();
  const { createSupplierNotification, getSNFeatureConfiguration, getExternalOrdersByModule, getOneCGLocationV2 } = useContext(ApiContext);
  const { BackendValidationComponent, setBeValidationErrors } = useBEValidation();
  const [snConfiguration, setSnConfiguration] = useState<SnPartyConfigDTO>();
  // RD-2977: folder switches of the caller's company (BE resolves them for the logged-in company)
  const [snFolderSettings, setSnFolderSettings] = useState<SnFolderSettingsDTO>();
  const accessibleSnFolders = useFolders(EntityTypeEnum.SUPPLIER_NOTIFICATION);
  // PAP external orders: company-level setting (common) gates fetching the real upstream orders for the picker.
  const [externalOrdersApiType, setExternalOrdersApiType] = useState<ExternalOrdersApiTypeEnum>();
  const [externalOrdersResponse, setExternalOrdersResponse] = useState<GetExternalOrdersResponse>();
  // Recomputed on every external-orders fetch (supplier/location change): true when PAP is mapped but the upstream
  // returns only the NO_ORDERS_NOW sentinel → no order to link for this carrier + location, so creation is blocked.
  const [noExternalOrdersAvailable, setNoExternalOrdersAvailable] = useState(false);

  const names = proxiedPropertiesOf<SupplierNotificationForm>();

  const save = (values: SupplierNotificationForm) => {
    setLoading(true);
    createSupplierNotification(
      {
        labelIds: (values.labels ?? []).map(l => l.labelId),
        notifyData: {
          responsiblePersonId: values.notifyData?.responsiblePerson?.id,
          userIdsToNotify: values.notifyData?.userToNotify?.map(u => u.id),
          notifyUsersDependsOnRole: values.notifyData?.notifyUsersDependsOnRole,
        },
        deliveryConditionsIncotermCode: values.deliveryConditionsIncotermCode,
        customsTypeCode: values.customsTypeCode,
        orders: values.orders.map(o => mapSnOrder(o, externalIdPresent)),
        containerInfo: values.containerInfo,
        companyId: values.companyId,
        cmrNumber: values.cmrNumber,
        blNumber: values.blNumber,
        awbNumber: values.awbNumber,
        // Mirror the active typed doc field to the deprecated shipmentId; don't fall back to the stale seeded value
        // (duplication seeds shipmentId from the source, so a cleared doc field must clear it too).
        shipmentId: activeDocNumber(values),
        cargoType: values.cargoType,
        attachments: values.attachments?.map(t => ({
          attachmentId: t.attachmentId,
          documentType: t.documentType,
          accessType: t.accessType || AccessTypeEnum.ALL,
          labelIds: t.labels?.map(l => l.labelId) ?? [],
          userNote: t.userNote,
          userDateLocalDate: t.userDateLocalDate ?? null,
        })) ?? [],
        customerLocationId: values.customerLocationId,
        internalInvoiceNumber: values.goodsInvoice?.goodsInvoiceNumber,
        invoiceDueDate: values.goodsInvoice?.goodsInvoiceDate ?? null,
        shipmentStateCode: values.shipmentStateCode,
        supplierId: values.supplierId,
        supplierLocationId: values.supplierLocationId,
        referralCode: values.referralCode,
        specialRequirementIds: values.specialRequirementIds,
        transportationType: values.transportationType,
        loadingGlobalTimeslots: values.supplierDeliverySlots,
        unloadingGlobalTimeslots: values.customerDeliverySlots,
        fromShipmentId: values.fromShipmentId,
        internalNote: values.internalNote,
        note: values.note,
        counterPartyNotifyPersonsIds: values.counterPartyNotifyPersonsIds,
        folderIds: (values.folders ?? []).map(f => f.id),
      },
      () => {
        if (isEntityOwner(loggedUser, values.companyId)) {
          navigation.navigate(navigation.urlFunctions.createSupplierNotificationList());
        } else {
          redirectWithToastMessage(
            '/supplier-notifications',
            tr(`CreateSupplierNotification.snCreated`, `SN created`),
            tr('CreateSupplierNotification.snCreatedDetailWithCompanyName', 'SN created and company {{companyName}} will contact you soon.', {
              companyName: loggedUser.modules.find(t => t.owner?.companyId === values.companyId).owner.companyName,
            }),
            'success',
          );
        }
      },
      null,
      {
        onValidationFailed: d => {
          setLoading(false);
          setBeValidationErrors(d);
        },
        onError: (_error, showStandardError) => {
          setLoading(false);
          showStandardError();
        },
      },
    );
  };

  // PAP picker is active only when the integration AND this location are mapped — then the first field links external orders.
  const externalIdPresent = snExternalIdPresent(externalOrdersResponse);

  // Orders validation comes from the shared framework schema, driven by the SN order config (amount/value/cargo/item-note).
  const orderValidation = createOrdersComponentValidationSchema(generateSnOrderConfig(snConfiguration), false, tr, externalIdPresent);

  // Timeslot date is mandatory only when company policy marks it required (FieldMode); visibility is gated by the LocationCard's secured overlay.
  const requireTimeslots = snConfiguration?.timeslots?.required === true;

  // Function-form schema so per-tag `required` tracks the live config + visibleWhen/requiredWhen on each validation.
  const validationSchema = (formData: SupplierNotificationForm) => {
    const c = resolveSnTagConfig(snConfiguration, tagValueOf(formData));
    const docRequired = (key: TagEnum, value: string) => !c.isRequired(key) || isNotBlank(value);
    const arrayRequired = (key: TagEnum, value: unknown[]) => !c.isRequired(key) || (Array.isArray(value) && value.length > 0);
    return yup.object().shape({
      companyId: yup.string().required(),
      orders: yup.array().of(orderValidation),
      shipmentStateCode: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.STATUS, value)),
      transportationType: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.TRANSPORTATION_TYPE, value)),
      referralCode: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.LOADING_REFERENCE, value)),
      note: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.CARRIER_NOTE, value)),
      specialRequirementIds: yup.array().nullable().test('required', (value: any) => arrayRequired(TagEnum.CARRIER_REQUIREMENTS, value)),
      containerInfo: yup
        .object()
        .nullable()
        .test('required', tr('CreateSupplierNotification.containerRequired', 'Container is required'), (value: any) => {
          if (c.isRequired(TagEnum.CONTAINER)) return value != null && !!value.containerId && !!value.containerInfoCode;
          if (value) return !!value.containerId && !!value.containerInfoCode;
          return true;
        }),
      customsTypeCode: yup.mixed().test('required', (value: any) => !c.isRequired(TagEnum.CUSTOMS_REGIME) || value != null),
      cmrNumber: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.CMR_NUMBER, value)),
      blNumber: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.BL_NUMBER, value)),
      awbNumber: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.AWB_NUMBER, value)),
      supplierId: yup.string().required(),
      supplierLocation: yup.mixed().required(),
      customerLocation: yup.mixed().required(),
      supplierDeliverySlots: timeslotValidationSchema(requireTimeslots),
      customerDeliverySlots: timeslotValidationSchema(requireTimeslots),
      cargoType: yup.object().shape({
        cargoTypeCode: yup.string().nullable().test('required', (value: any) => docRequired(TagEnum.CARGO, value)),
        adrWeight: yup.number().when('cargoTypeCode', {
          is: CargoTypeCodeEnum.ADR_LQ,
          then: schema => schema.required(),
          otherwise: schema => schema.notRequired().nullable(),
        }),
      }),
      // Notify recipient is mandatory only when the company config requires it (BE enforces the same).
      counterPartyNotifyPersonsIds: yup
        .array()
        .test('required', tr('CreateSupplierNotification.whoToNotifyRequired', 'Please select whom to notify'), value =>
          snConfiguration?.requestNotifiedPersonInSn?.required !== true || (Array.isArray(value) && value.length > 0),
        ),
    });
  };

  const childrenForms = useRef([]);
  const form = useForm<SupplierNotificationForm>(
    validationSchema,
    initialData,
    values => save(values),
    false,
    false,
    () => childrenForms.current.forEach(t => t?.validate()),
  );

  // Location notes are appended to the carrier note when a location is picked. The note is user-owned free text,
  // so a changed location appends its note and leaves the previous one for the user to remove. The note isn't on
  // the SN location picker DTO, so we re-fetch it via getOneCGLocationV2.
  const applyLocationNote = (party: 'loading' | 'unloading', locationId?: string, name?: string) => {
    if (!locationId) return;
    getOneCGLocationV2(locationId, data => {
      const stop = { name: data.locationName ?? name, locationNote: data.locationNote };
      form.setFieldValue(names.note, buildCarrierNoteWithLocationBlock(form.values.note, [stop]));
    });
  };

  // Prefill/duplicate seeds the locations programmatically (not via the pickers), so mirror the pick path once on
  // mount and append their notes to whatever the note already holds.
  const locationNotePrefilled = useRef(false);
  useEffect(() => {
    if (locationNotePrefilled.current) return;
    locationNotePrefilled.current = true;

    const locationIds = [form.values.supplierLocationId, form.values.customerLocationId].filter(isNotNullOrUndefined);
    if (locationIds.length === 0) return;

    Promise.all(
      locationIds.map(
        id =>
          new Promise<{ name?: string; locationNote?: string }>(resolve =>
            getOneCGLocationV2(
              id,
              data => resolve({ name: data.locationName, locationNote: data.locationNote }),
              null,
              { onError: () => resolve({}), onEntityNotFound: () => resolve({}) },
            ),
          ),
      ),
    ).then(stops => form.setFieldValue(names.note, buildCarrierNoteWithLocationBlock(form.values.note, stops)));
  }, []);

  const isOwner = loggedUser?.companyId === form.values.companyId;
  // SN write capability — single source of truth (RD-2738: permission-based); all call sites use canEditSn/disabled,
  // and the notify-persons LOV filters by the same permission.
  const canEditSn = hasPermission([PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE]);
  const disabled = !canEditSn;

  // Per-tag config (visible/editable/required, incl. visibleWhen/requiredWhen evaluated against current form values).
  const cfg = resolveSnTagConfig(snConfiguration, tagValueOf(form.values));
  const tagReadOnly = (key: TagEnum) => disabled || !cfg.isEditable(key);
  // Route each tag's visibility/editability through the security overlay, with the company policy as a custom guard.
  const tagSecured = (key: TagEnum): SecuredConfig => ({
    id: 'sn-create-tag',
    instance: key,
    description: `Supplier notification field ${key}; the customer's notification settings decide whether it is shown and editable`,
    visible: [{ type: 'custom', label: tr('CreateSupplierNotification.policyVisible', 'Visible by company policy'), satisfied: cfg.isVisible(key), description: POLICY_DESCRIPTION.visible, byDesign: false }],
    editable: [
      { type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE], satisfied: canEditSn },
      { type: 'custom', label: tr('CreateSupplierNotification.policyEditable', 'Editable by company policy'), satisfied: cfg.isEditable(key), description: POLICY_DESCRIPTION.editable, byDesign: false },
    ],
  });

  // Timeslot visibility from company policy (FieldMode.visible), surfaced in the security overlay. Editability not enforced yet (ManageTimeslot has no disabled).
  const timeslotSecured = (id: string): SecuredConfig => ({
    id,
    description: 'Requested loading or unloading time window of the notification',
    visible: [{ type: 'custom', label: tr('CreateSupplierNotification.policyVisible', 'Visible by company policy'), satisfied: snConfiguration?.timeslots?.visible ?? true, description: POLICY_DESCRIPTION.visible, byDesign: false }],
  });

  // Supplier/customer party selector — company policy (supplierAndCustomer FieldMode). Default visible+editable when config absent so the customer can be picked (which loads the config). The edit-only READONLY rule lives on the Detail/Header path, not here.
  const partySecured = (id: string): SecuredConfig => ({
    id,
    description: 'Supplier or customer company, or its location, on the loading or unloading side of the notification',
    visible: [{ type: 'custom', label: tr('CreateSupplierNotification.policyVisible', 'Visible by company policy'), satisfied: snConfiguration?.supplierAndCustomer?.visible ?? true, description: POLICY_DESCRIPTION.visible, byDesign: false }],
    editable: [
      { type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE], satisfied: canEditSn },
      { type: 'custom', label: tr('CreateSupplierNotification.policyEditable', 'Editable by company policy'), satisfied: snConfiguration?.supplierAndCustomer?.editable ?? true, description: POLICY_DESCRIPTION.editable, byDesign: false },
    ],
  });

  // Config defaults prefill every tag — but only when the field is empty and the tag is visible (source-prefill > config default > empty).
  useEffect(() => {
    if (isNullOrUndefined(snConfiguration)) return;
    const v = form.values;
    const appliers: { key: TagEnum; empty: boolean; apply: (codes: string[]) => void }[] = [
      { key: TagEnum.STATUS, empty: isNullOrUndefined(v.shipmentStateCode), apply: c => form.setFieldValue(names.shipmentStateCode, c[0]) },
      { key: TagEnum.TRANSPORTATION_TYPE, empty: isNullOrUndefined(v.transportationType), apply: c => form.setFieldValue(names.transportationType, c[0]) },
      { key: TagEnum.CARGO, empty: isNullOrUndefined(v.cargoType?.cargoTypeCode), apply: c => form.setFieldValue(names.cargoType, { cargoTypeCode: c[0] }) },
      { key: TagEnum.CUSTOMS_REGIME, empty: isNullOrUndefined(v.customsTypeCode), apply: c => form.setFieldValue(names.customsTypeCode, c[0]) },
      { key: TagEnum.CMR_NUMBER, empty: !isNotBlank(v.cmrNumber), apply: c => form.setFieldValue(names.cmrNumber, c[0]) },
      { key: TagEnum.BL_NUMBER, empty: !isNotBlank(v.blNumber), apply: c => form.setFieldValue(names.blNumber, c[0]) },
      { key: TagEnum.AWB_NUMBER, empty: !isNotBlank(v.awbNumber), apply: c => form.setFieldValue(names.awbNumber, c[0]) },
      { key: TagEnum.CONTAINER, empty: isNullOrUndefined(v.containerInfo?.containerInfoCode), apply: c => form.setFieldValue(names.containerInfo, { ...v.containerInfo, containerInfoCode: c[0] }) },
      { key: TagEnum.LOADING_REFERENCE, empty: !isNotBlank(v.referralCode), apply: c => form.setFieldValue(names.referralCode, c[0]) },
      { key: TagEnum.CARRIER_REQUIREMENTS, empty: (v.specialRequirementIds?.length ?? 0) === 0, apply: c => form.setFieldValue(names.specialRequirementIds, c) },
      { key: TagEnum.CARRIER_NOTE, empty: !isNotBlank(v.note), apply: c => form.setFieldValue(names.note, c[0]) },
    ];
    appliers.forEach(a => {
      const codes = cfg.defaultValue(a.key);
      if (codes.length > 0 && a.empty && cfg.isVisible(a.key)) a.apply(codes);
    });
  }, [snConfiguration]);

  // Internal info is visible only to the notification owner; editable when the logged company owns it and has SN write rights.
  const showInternalInfo = useSecuredFlag('sn-create-internal-info', {
    kind: 'visibility',
    description: 'Owner only: other companies, e.g. a supplier filling it in for its customer, do not see it',
    byDesign: true,
    ownership: { label: 'logged company owns the notification', satisfied: isOwner },
  });
  const editInternalInfo = useSecuredFlag('sn-create-edit-internal-info', {
    kind: 'editability',
    description: 'Owner only: only the company the notification is for edits its internal info',
    byDesign: true,
    ownership: { label: 'logged company is the entity owner', satisfied: isOwner },
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE],
  });

  useEffect(() => {
    if (isNotNullOrUndefined(form.values.companyId)) {
      getSNFeatureConfiguration(form.values.companyId, d => {
        setSnConfiguration(d.config);
        setSnFolderSettings(d.folders);
        setExternalOrdersApiType(d.common?.externalOrdersApiType);
      });
    }
  }, [form.values.companyId]);

  // Fetch real upstream PAP orders for the picker once the company is PAP-enabled and customer location + supplier are chosen.
  // The "no orders available" flag is recomputed from each fresh response so it tracks supplier/location changes.
  useEffect(() => {
    if (isSnPapEnabled(externalOrdersApiType, form.values.companyId, form.values.supplierId, form.values.customerLocationId)) {
      getExternalOrdersByModule('SUPPLIER_NOTIFICATION', form.values.companyId, form.values.supplierId, form.values.customerLocationId, d => {
        setExternalOrdersResponse(d);
        // Block only when the PAP integration + location are mapped (externalIdPresent && externalIdLocationPresent);
        // otherwise the form falls through to normal order entry, so the NO_ORDERS_NOW sentinel doesn't apply.
        setNoExternalOrdersAvailable(snExternalIdPresent(d) && hasOnlyNoOrdersNow(d?.orders));
      });
    } else {
      setExternalOrdersResponse(undefined);
      setNoExternalOrdersAvailable(false);
    }
  }, [externalOrdersApiType, form.values.companyId, form.values.supplierId, form.values.customerLocationId]);

  useEffect(() => {
    if (isNotNullOrUndefined(form.values.supplierLocation) && snConfiguration) {
      const isEU = europeanUnion.includes(form.values.supplierLocation.countryCode3);
      if (!isEU && form.values.customsTypeCode == null && cfg.isRequired(TagEnum.CUSTOMS_REGIME)) {
        form.setFieldValue(names.customsTypeCode, CustomsTypeCodeEnum.UNDER_CUSTOMS);
      }
    }
  }, [form.values.supplierLocation, form.values.customsTypeCode, snConfiguration]);

  // Convenience prefill: default the owner's notify contact to the current user — but ONLY when the field is visible.
  // Never set a value behind a disabled field: a value in counterPartyNotifyPersonsIds triggers a notification email.
  useEffect(() => {
    if (isOwner && snConfiguration?.requestNotifiedPersonInSn?.visible === true && (form.values.counterPartyNotifyPersonsIds?.length ?? 0) === 0 && loggedUser?.id) {
      form.setFieldValue(names.counterPartyNotifyPersonsIds, [loggedUser.id]);
    }
  }, [isOwner, snConfiguration]);

  // RD-2977: a single accessible folder is pre-selected when folders are required
  useEffect(() => {
    if (snFolderSettings?.required !== true) return;
    if (accessibleSnFolders.length === 1 && (form.values.folders?.length ?? 0) === 0) {
      form.setFieldValue(names.folders, [accessibleSnFolders[0]]);
    }
  }, [snFolderSettings?.required, accessibleSnFolders]);

  // Notify-person dropdown — company policy (requestNotifiedPersonInSn FieldMode). Hidden until config known; owner default is DISABLED, inheritor REQUIRED (replaces the old isOwner/companyId hardcode).
  const notifySecured: SecuredConfig = {
    id: 'sn-notify-person',
    description: "Who at the customer is notified about the new notification; the customer's settings decide whether it is asked for",
    visible: [{ type: 'custom', label: tr('CreateSupplierNotification.policyVisible', 'Visible by company policy'), satisfied: snConfiguration?.requestNotifiedPersonInSn?.visible ?? false, description: POLICY_DESCRIPTION.visible, byDesign: false }],
    editable: [
      { type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE], satisfied: canEditSn },
      { type: 'custom', label: tr('CreateSupplierNotification.policyEditable', 'Editable by company policy'), satisfied: snConfiguration?.requestNotifiedPersonInSn?.editable ?? false, description: POLICY_DESCRIPTION.editable, byDesign: false },
    ],
  };
  const notifyDecoration = useSecuredDecoration(notifySecured);

  // Per-field order overlay — shared with the detail sidebar; generic key → SN policy FieldMode.
  const orderFieldSecured = makeSnOrderFieldSecured(snConfiguration, { type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE], satisfied: canEditSn }, tr, 'sn-order');

  if (isNullOrUndefined(loggedUser) || isNullOrUndefined(form?.values)) return <></>;

  return (
    <div className={'flex flex-col gap-4'}>
      <BreadCrumbComponent
        items={[
          {
            label: form.values.shipmentId
              ? tr(`CreateSupplierNotification.followingSupplierNotification`, `Following supplier notification`)
              : tr(`CreateSupplierNotification.newSupplierNotification`, `New supplier notification`),
          },
        ]}
      />
      <BackendValidationComponent />

      <Heading
        title={tr('CreateSupplierNotification.newSupplierNotification', 'New supplier notification')}
        headingVariant={'h1'}
        labels={
          <LabelsTag
            objectId={null}
            objectType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            resolveLabelsForType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
            value={form.values.labels}
            onChange={labels => form.setFieldValue(names.labels, labels)}
            disabled={disabled}
            dataTestId={'sn-header-labels'}
          />
        }
        buttons={[]}
      />

      {/* The notification — loading & unloading address cards side by side */}
      <SectionPanel icon={<SvgCalendar />} heading={tr('CreateSupplierNotification.theNotification', 'The notification')}>
        <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4 items-start'}>
            <LocationCard
              heading={tr('LocationCard.loadingAddress', 'Loading address')}
              icon={<SvgMapPin />}
              disabled={disabled}
              partySelector={{
                role: 'consignor',
                companyId: form.values.companyId,
                inheritorId: form.values.supplierId,
                onChangeCompany: v => form.setFieldValue(names.supplierId, v),
                secured: partySecured('sn-loading-party'),
                required: snConfiguration?.supplierAndCustomer?.required === true,
              }}
              partyLabel={tr('LocationCard.sender', 'Sender')}
              partyErrorMessage={findValidationMessage(form.validationErrors, 'supplierId', tr)}
              partyDataTestId={'sn-header-consignor'}
              allowManualEntry={false}
              location={{
                companyId: form.values.supplierId,
                companyLocationId: form.values.supplierLocationId,
                onChange: v => {
                  form.setFieldValue(names.supplierLocationId, v?.id);
                  form.setFieldValue(names.supplierLocation, v);
                  applyLocationNote('loading', v?.id, v?.name);
                },
                secured: partySecured('sn-loading-location'),
                dataTestId: 'sn-header-consignorLocation',
              }}
              locationLabel={tr('generic.locationName', 'Location name')}
              locationErrorMessage={findValidationMessage(form.validationErrors, 'supplierLocation', tr)}
              timeslot={{
                entityId: form.values.supplierLocationId,
                value: form.values.supplierDeliverySlots,
                onChange: v => form.setFieldValue(names.supplierDeliverySlots, { ...v, timezoneId: form.values.supplierLocation?.timezoneId }),
                validationErrors: form.validationErrors,
                validationPrefix: 'supplierDeliverySlots.',
                required: requireTimeslots,
                secured: timeslotSecured('sn-loading-timeslot'),
                dataTestId: 'sn-header-consignorTimeslot',
              }}
              timeslotLabel={tr('LocationCard.pickupDate', 'Pickup date')}
            />

            <LocationCard
              heading={tr('LocationCard.unloadingAddress', 'Unloading address')}
              icon={<SvgMapPin />}
              disabled={disabled}
              partySelector={{
                role: 'consignee',
                companyId: form.values.companyId,
                onChangeCompany: c => form.setFieldValue(names.companyId, c),
                onChangeCompanyCode: v => form.setFieldValue('companyCode', v),
                secured: partySecured('sn-unloading-party'),
                required: snConfiguration?.supplierAndCustomer?.required === true,
              }}
              partyLabel={tr('LocationCard.recipient', 'Recipient')}
              partyDataTestId={'sn-header-consignee'}
              allowManualEntry={false}
              location={{
                companyId: form.values.companyId,
                companyLocationId: form.values.customerLocationId,
                onChange: v => {
                  form.setFieldValue(names.customerLocationId, v?.id);
                  form.setFieldValue(names.customerLocation, v);
                  applyLocationNote('unloading', v?.id, v?.name);
                },
                secured: partySecured('sn-unloading-location'),
                dataTestId: 'sn-header-consigneeLocation',
              }}
              locationLabel={tr('generic.locationName', 'Location name')}
              locationErrorMessage={findValidationMessage(form.validationErrors, 'customerLocation', tr)}
              timeslot={{
                entityId: form.values.customerLocationId,
                value: form.values.customerDeliverySlots,
                onChange: v => form.setFieldValue(names.customerDeliverySlots, { ...v, timezoneId: form.values.customerLocation?.timezoneId }),
                validationErrors: form.validationErrors,
                validationPrefix: 'customerDeliverySlots.',
                required: requireTimeslots,
                secured: timeslotSecured('sn-unloading-timeslot'),
                dataTestId: 'sn-header-consigneeTimeslot',
              }}
              timeslotLabel={tr('LocationCard.deliveryDate', 'Delivery date')}
              extrasSlot={notifyDecoration.decorate(
                <Field
                  label={tr('CreateSupplierNotification.notifyRecipientWhenCreated', 'Notify recipient when created')}
                  required={snConfiguration?.requestNotifiedPersonInSn?.required === true && (form.values.counterPartyNotifyPersonsIds?.length ?? 0) === 0}
                  errorMessage={form.errors?.counterPartyNotifyPersonsIds as string | undefined}
                >
                  <div className={'w-full'}>
                    <InputCounterPartyNotifyPersonsIds
                      companyId={form.values.companyId}
                      permission={PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE}
                      value={form.values.counterPartyNotifyPersonsIds || []}
                      onChange={ids => form.setFieldValue(names.counterPartyNotifyPersonsIds, ids)}
                      disabled={disabled || notifyDecoration.editable === false}
                      style={{ width: '100%' }}
                      dataTestId={'sn-counterPartyNotifyPersons'}
                    />
                  </div>
                </Field>,
              )}
            />
        </div>
      </SectionPanel>

      {/* Shipping information — single flowing wrap tags reflow to fill width. Order is fixed; visibility/required from config. */}
      <COHeadline variant={'h2'} title={tr('CreateSupplierNotification.shippingInformation', 'Shipping Information')} icon={<SvgLkw />} />
      <div className={'flex flex-row flex-wrap gap-2'}>
        <ShipmentStatusTag secured={tagSecured(TagEnum.STATUS)} code={form.values.shipmentStateCode} onChange={v => form.setFieldValue(names.shipmentStateCode, v)} required={cfg.isRequired(TagEnum.STATUS)} readOnly={tagReadOnly(TagEnum.STATUS)} dataTestId={'sn-header-shipmentStatus'} />
        <TransportationTypeTag secured={tagSecured(TagEnum.TRANSPORTATION_TYPE)} transportationType={form.values.transportationType} onChange={v => form.setFieldValue(names.transportationType, v)} required={cfg.isRequired(TagEnum.TRANSPORTATION_TYPE)} readOnly={tagReadOnly(TagEnum.TRANSPORTATION_TYPE)} />
        <CargoTypeTag secured={tagSecured(TagEnum.CARGO)} cargoType={form.values.cargoType} onChange={v => form.setFieldValue(names.cargoType, v)} required={cfg.isRequired(TagEnum.CARGO)} readOnly={tagReadOnly(TagEnum.CARGO)} errorMessage={findValidationMessage(form.validationErrors, 'cargoType', tr)} />
        <CustomsRegimeTag
          secured={tagSecured(TagEnum.CUSTOMS_REGIME)}
          value={form.values.customsTypeCode}
          onChange={v => form.setFieldValue(names.customsTypeCode, v)}
          required={cfg.isRequired(TagEnum.CUSTOMS_REGIME)}
          readOnly={tagReadOnly(TagEnum.CUSTOMS_REGIME)}
          errorMessage={findValidationMessage(form.validationErrors, 'customsTypeCode', tr)}
          dataTestId={'sn-cargoInfo-customsRegime'}
        />
        <InputTextTag secured={tagSecured(TagEnum.CMR_NUMBER)} label={tr('SupplierNotificationDetail.cmrNumber', 'CMR Number')} value={form.values.cmrNumber} onChange={v => form.setFieldValue(names.cmrNumber, v)} required={cfg.isRequired(TagEnum.CMR_NUMBER)} readOnly={tagReadOnly(TagEnum.CMR_NUMBER)} errorMessage={findValidationMessage(form.validationErrors, 'cmrNumber', tr)} dataTestId={'sn-cargoInfo-cmrId'} />
        <InputTextTag secured={tagSecured(TagEnum.BL_NUMBER)} label={tr('CreateSupplierNotification.blNumber', 'BL Number')} value={form.values.blNumber} onChange={v => form.setFieldValue(names.blNumber, v)} required={cfg.isRequired(TagEnum.BL_NUMBER)} readOnly={tagReadOnly(TagEnum.BL_NUMBER)} errorMessage={findValidationMessage(form.validationErrors, 'blNumber', tr)} dataTestId={'sn-cargoInfo-blNumber'} />
        <InputTextTag secured={tagSecured(TagEnum.AWB_NUMBER)} label={tr('CreateSupplierNotification.awbNumber', 'AWB Number')} value={form.values.awbNumber} onChange={v => form.setFieldValue(names.awbNumber, v)} required={cfg.isRequired(TagEnum.AWB_NUMBER)} readOnly={tagReadOnly(TagEnum.AWB_NUMBER)} errorMessage={findValidationMessage(form.validationErrors, 'awbNumber', tr)} dataTestId={'sn-cargoInfo-awbNumber'} />
        <ContainerTag secured={tagSecured(TagEnum.CONTAINER)} value={form.values.containerInfo} onChange={v => form.setFieldValue(names.containerInfo, v)} required={cfg.isRequired(TagEnum.CONTAINER)} readOnly={tagReadOnly(TagEnum.CONTAINER)} validationErrors={form.validationErrors} dataTestId={'sn-cargoInfo-containerSpecs'} />
        <InputTextTag secured={tagSecured(TagEnum.LOADING_REFERENCE)} label={tr('CreateShipment.referralCode', 'Loading reference')} value={form.values.referralCode} onChange={v => form.setFieldValue(names.referralCode, v)} required={cfg.isRequired(TagEnum.LOADING_REFERENCE)} readOnly={tagReadOnly(TagEnum.LOADING_REFERENCE)} errorMessage={findValidationMessage(form.validationErrors, 'referralCode', tr)} dataTestId={'sn-cargoInfo-loadingReference'} />
        <RequirementsTag
          secured={tagSecured(TagEnum.CARRIER_REQUIREMENTS)}
          valueCodes={form.values.specialRequirementIds}
          onChange={v => form.setFieldValue(names.specialRequirementIds, v)}
          required={cfg.isRequired(TagEnum.CARRIER_REQUIREMENTS)}
          readOnly={tagReadOnly(TagEnum.CARRIER_REQUIREMENTS)}
          errorMessage={findValidationMessage(form.validationErrors, 'specialRequirementIds', tr)}
          companyId={form.values.companyId}
          prefillEnabled={true}
          dataTestId={'sn-cargoInfo-carrierRequirements'}
        />
        <CarrierNoteTag secured={tagSecured(TagEnum.CARRIER_NOTE)} value={form.values.note} onChange={v => form.setFieldValue(names.note, v)} required={cfg.isRequired(TagEnum.CARRIER_NOTE)} readOnly={tagReadOnly(TagEnum.CARRIER_NOTE)} errorMessage={findValidationMessage(form.validationErrors, 'note', tr)} dataTestId={'sn-cargoInfo-note'} />
      </div>

      {/* Orders — shared framework component (same as expedition); kept left-aligned, not full width. */}
      <SecuredArea
        id={'sn-create-orders'}
        label={tr('CreateSupplierNotification.orders', 'Orders')}
        description={'The orders, handling units and goods the notification announces'}
        editable={[{ type: 'permission', anyOf: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE], satisfied: canEditSn }]}>
        <div className={'flex flex-col gap-4 w-full lg:w-1/2'}>
          <COHeadline variant={'h2'} title={tr('CreateSupplierNotification.orders', 'Orders')} />
          <Card>
            {noExternalOrdersAvailable ? (
              <COInlineMessage
                variant={'alert'}
                icon={
                  <span className={'text-content-tertiary'}>
                    <SvgAlert />
                  </span>
                }
                dataTestId={'sn-noExternalOrdersAvailable'}
                diagnostic={{ key: 'CreateSupplierNotification.noExternalOrdersAvailable' }}
              >
                <span className={'text-content-primary'}>
                  {tr(
                    'CreateSupplierNotification.noExternalOrdersAvailable',
                    'No order is available for the selected carrier and location. A supplier notification cannot be created.',
                  )}
                </span>
              </COInlineMessage>
            ) : (
              <OrdersComponent
                orders={form.values.orders}
                companyId={form.values.companyId}
                onChange={orders => form.setFieldValue(names.orders, orders)}
                config={generateSnOrderConfig(snConfiguration)}
                validationErrors={form.validationErrors}
                buildOrderTestId={buildSnOrderTestId}
                buildManUnitTestId={buildSnManUnitTestId}
                externalOrders={externalOrdersResponse?.orders}
                externalIdPresent={externalIdPresent}
                showNoOrdersNow={false}
                disableOrdersOutsideCurrentDate={true}
                disabled={disabled}
                fieldSecured={orderFieldSecured}
              />
            )}
          </Card>
        </div>
      </SecuredArea>

      <AttachmentsSection attachments={form.values.attachments} onChange={v => form.setFieldValue(names.attachments, v)} disabled={disabled} hideUpload={disabled} />

      <SecuredArea
        id={'sn-create-internal-info'}
        label={tr('CreateSupplierNotification.internalInfo', 'Internal info')}
        description={"The notification owner's internal details: responsible person, who to notify, goods invoice, delivery terms, internal note"}
        visible={showInternalInfo}
        editable={editInternalInfo}>
        <div className={'flex flex-col gap-4'}>
          <COHeadline variant={'h2'} title={tr('CreateSupplierNotification.internalInfo', 'Internal info')} icon={<SvgInternalInfoHeadling />} />
          <div className={'flex flex-row flex-wrap gap-2'}>
            <ResponsiblePersonTag person={form.values.notifyData?.responsiblePerson} onChange={v => form.setFieldValue('notifyData.responsiblePerson', v)} readonly={disabled} />
            <InternalInfoNotifyTag persons={form.values.notifyData?.userToNotify} onChange={v => form.setFieldValue('notifyData.userToNotify', v)} readonly={disabled} />
            <InternalInfoNotifyByRoleTag
              notifyRoleName={RoleEnum.ROLE_SN_NOTIFY}
              isActive={form.values.notifyData?.notifyUsersDependsOnRole}
              onChange={v => form.setFieldValue('notifyData.notifyUsersDependsOnRole', v)}
              readonly={disabled}
            />
            <InternalInfoGoodsInvoiceTag value={form.values.goodsInvoice} onChange={v => form.setFieldValue(names.goodsInvoice, v)} readonly={disabled} validationErrors={form.validationErrors} validationPrefix={'goodsInvoice'} />
            <IncotermCodeTag code={form.values.deliveryConditionsIncotermCode} onChange={v => form.setFieldValue(names.deliveryConditionsIncotermCode, v)} readOnly={disabled} dataTestId={'sn-internalInfo-deliveryTerm'} />
            <InternalInfoFoldersTag
              folders={form.values.folders ?? []}
              required={snFolderSettings?.required === true}
              errorMessage={findValidationMessage(form.validationErrors, 'folders', tr)}
              onChange={v => form.setFieldValue(names.folders, v)}
              entityType={EntityTypeEnum.SUPPLIER_NOTIFICATION}
              readonly={disabled}
            />
            <InternalNoteTag value={form.values.internalNote} onChange={v => form.setFieldValue(names.internalNote, v)} readonly={disabled} required={false} />
          </div>
        </div>
      </SecuredArea>

      <div className={'flex flex-row gap-8 items-center'} data-testid={'sn-save'}>
        <RingilButton
          label={tr('CreateSupplierNotification.create', 'Create')}
          mode={'filled-rectangle'}
          size={'big'}
          loading={loading}
          onClick={() => form.validateAndSend()}
          disabled={disabled || loading || noExternalOrdersAvailable}
        />
      </div>
    </div>
  );
};

export default CreateSupplierNotification;
